import type { DbModelDefinition, DbModelField } from '@/services/dbManager.service';

export type FilterLogic = 'AND' | 'OR';

export type FilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'like'
  | 'ilike'
  | 'notLike'
  | 'notILike'
  | 'in'
  | 'notIn'
  | 'is'
  | 'isNot';

export interface FilterRule {
  id: string;
  type: 'rule';
  field: string;
  operator: FilterOperator;
  value: string;
}

export interface FilterGroup {
  id: string;
  type: 'group';
  logic: FilterLogic;
  children: FilterNode[];
}

export type FilterNode = FilterRule | FilterGroup;

export const NULL_FILTER_VALUE = '__NULL__';

const INVALID_VALUE = Symbol('invalid-filter-value');

function createFilterId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isNumericType(type: string): boolean {
  return ['Int', 'BigInt', 'Float', 'Decimal'].includes(type);
}

function isDateTimeType(type: string): boolean {
  return type === 'DateTime';
}

function isBooleanType(type: string): boolean {
  return type === 'Boolean';
}

function isRule(node: FilterNode): node is FilterRule {
  return node.type === 'rule';
}

export function createEmptyFilterRule(field = ''): FilterRule {
  return {
    id: createFilterId(),
    type: 'rule',
    field,
    operator: 'eq',
    value: '',
  };
}

export function createEmptyFilterGroup(logic: FilterLogic = 'AND'): FilterGroup {
  return {
    id: createFilterId(),
    type: 'group',
    logic,
    children: [],
  };
}

export function createEqualityFilterGroup(field: string, value: unknown): FilterGroup {
  const group = createEmptyFilterGroup('AND');
  return {
    ...group,
    children: [
      {
        id: createFilterId(),
        type: 'rule',
        field,
        operator: 'eq',
        value: toRuleInputValue(value),
      },
    ],
  };
}

function toRuleInputValue(value: unknown): string {
  if (value === null || value === undefined) return NULL_FILTER_VALUE;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => String(item)).join(', ');

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function toDateTimeLocal(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function normalizePrimitiveRuleValue(value: unknown, fieldMeta?: DbModelField): string {
  if (value === null) return NULL_FILTER_VALUE;
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);

  if (typeof value === 'string') {
    if (fieldMeta && isDateTimeType(fieldMeta.type)) {
      return toDateTimeLocal(value);
    }

    return value;
  }

  return toRuleInputValue(value);
}

function parseCsvValue(rawValue: string, fieldMeta?: DbModelField): unknown[] | typeof INVALID_VALUE {
  if (!rawValue.trim()) return INVALID_VALUE;

  const values = rawValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (values.length === 0) return INVALID_VALUE;

  return values.map((value) => parseScalarValue(value, fieldMeta, 'eq')).filter((item) => item !== INVALID_VALUE);
}

function parseScalarValue(rawValue: string, fieldMeta: DbModelField | undefined, operator: FilterOperator): unknown | typeof INVALID_VALUE {
  if (operator === 'is' || operator === 'isNot') {
    if (rawValue === NULL_FILTER_VALUE || rawValue === '') return null;
  }

  if (!fieldMeta) {
    return rawValue;
  }

  if (isBooleanType(fieldMeta.type)) {
    if (rawValue === 'true') return true;
    if (rawValue === 'false') return false;
    if (rawValue === NULL_FILTER_VALUE) return null;
    return INVALID_VALUE;
  }

  if (isNumericType(fieldMeta.type)) {
    const num = Number(rawValue);
    if (Number.isFinite(num)) return num;
    return INVALID_VALUE;
  }

  if (isDateTimeType(fieldMeta.type)) {
    if (rawValue === NULL_FILTER_VALUE) return null;
    const date = new Date(rawValue);
    if (Number.isNaN(date.getTime())) return INVALID_VALUE;
    return date.toISOString();
  }

  if (rawValue === NULL_FILTER_VALUE) {
    return null;
  }

  return rawValue;
}

function buildRuleClause(rule: FilterRule, modelDefinition?: DbModelDefinition | null): Record<string, unknown> | undefined {
  const fieldName = rule.field.trim();
  if (!fieldName) return undefined;

  const fieldMeta = modelDefinition?.fields.find((field) => field.name === fieldName);

  if (rule.operator === 'in' || rule.operator === 'notIn') {
    const parsedList = parseCsvValue(rule.value, fieldMeta);
    if (parsedList === INVALID_VALUE || parsedList.length === 0) return undefined;

    return {
      [fieldName]: {
        [rule.operator]: parsedList,
      },
    };
  }

  const parsedValue = parseScalarValue(rule.value, fieldMeta, rule.operator);
  if (parsedValue === INVALID_VALUE) return undefined;

  switch (rule.operator) {
    case 'eq':
      return parsedValue === null ? { [fieldName]: null } : { [fieldName]: parsedValue };
    case 'neq':
      return { [fieldName]: { not: parsedValue } };
    case 'gt':
      return { [fieldName]: { gt: parsedValue } };
    case 'gte':
      return { [fieldName]: { gte: parsedValue } };
    case 'lt':
      return { [fieldName]: { lt: parsedValue } };
    case 'lte':
      return { [fieldName]: { lte: parsedValue } };
    case 'like':
      return { [fieldName]: { contains: String(parsedValue ?? '') } };
    case 'ilike':
      return { [fieldName]: { contains: String(parsedValue ?? ''), mode: 'insensitive' } };
    case 'notLike':
      return { [fieldName]: { not: { contains: String(parsedValue ?? '') } } };
    case 'notILike':
      return { [fieldName]: { not: { contains: String(parsedValue ?? ''), mode: 'insensitive' } } };
    case 'is':
      return parsedValue === null ? { [fieldName]: null } : { [fieldName]: { equals: parsedValue } };
    case 'isNot':
      return { [fieldName]: { not: parsedValue } };
    default:
      return undefined;
  }
}

function buildNodeClause(node: FilterNode, modelDefinition?: DbModelDefinition | null): Record<string, unknown> | undefined {
  if (isRule(node)) {
    return buildRuleClause(node, modelDefinition);
  }

  return buildPrismaWhere(node, modelDefinition);
}

export function buildPrismaWhere(group: FilterGroup, modelDefinition?: DbModelDefinition | null): Record<string, unknown> | undefined {
  const clauses = group.children
    .map((node) => buildNodeClause(node, modelDefinition))
    .filter((clause): clause is Record<string, unknown> => Boolean(clause));

  if (clauses.length === 0) return undefined;

  return {
    [group.logic]: clauses,
  };
}

function parseConditionRule(fieldName: string, condition: unknown, modelDefinition?: DbModelDefinition | null): FilterRule | undefined {
  const fieldMeta = modelDefinition?.fields.find((field) => field.name === fieldName);

  if (condition === null || typeof condition !== 'object' || Array.isArray(condition)) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'eq',
      value: normalizePrimitiveRuleValue(condition, fieldMeta),
    };
  }

  const valueObject = condition as Record<string, unknown>;

  if (Array.isArray(valueObject.in)) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'in',
      value: valueObject.in.map((item) => String(item)).join(', '),
    };
  }

  if (Array.isArray(valueObject.notIn)) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'notIn',
      value: valueObject.notIn.map((item) => String(item)).join(', '),
    };
  }

  if (typeof valueObject.contains === 'string') {
    const insensitive = valueObject.mode === 'insensitive';
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: insensitive ? 'ilike' : 'like',
      value: valueObject.contains,
    };
  }

  if (Object.prototype.hasOwnProperty.call(valueObject, 'equals')) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'eq',
      value: normalizePrimitiveRuleValue(valueObject.equals, fieldMeta),
    };
  }

  if (Object.prototype.hasOwnProperty.call(valueObject, 'not')) {
    const notValue = valueObject.not;

    if (notValue && typeof notValue === 'object' && !Array.isArray(notValue)) {
      const notObject = notValue as Record<string, unknown>;
      if (typeof notObject.contains === 'string') {
        const insensitive = notObject.mode === 'insensitive';
        return {
          id: createFilterId(),
          type: 'rule',
          field: fieldName,
          operator: insensitive ? 'notILike' : 'notLike',
          value: notObject.contains,
        };
      }
    }

    if (notValue === null || typeof notValue !== 'object' || Array.isArray(notValue)) {
      return {
        id: createFilterId(),
        type: 'rule',
        field: fieldName,
        operator: 'isNot',
        value: normalizePrimitiveRuleValue(notValue, fieldMeta),
      };
    }
  }

  if (Object.prototype.hasOwnProperty.call(valueObject, 'gt')) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'gt',
      value: normalizePrimitiveRuleValue(valueObject.gt, fieldMeta),
    };
  }

  if (Object.prototype.hasOwnProperty.call(valueObject, 'gte')) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'gte',
      value: normalizePrimitiveRuleValue(valueObject.gte, fieldMeta),
    };
  }

  if (Object.prototype.hasOwnProperty.call(valueObject, 'lt')) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'lt',
      value: normalizePrimitiveRuleValue(valueObject.lt, fieldMeta),
    };
  }

  if (Object.prototype.hasOwnProperty.call(valueObject, 'lte')) {
    return {
      id: createFilterId(),
      type: 'rule',
      field: fieldName,
      operator: 'lte',
      value: normalizePrimitiveRuleValue(valueObject.lte, fieldMeta),
    };
  }

  return {
    id: createFilterId(),
    type: 'rule',
    field: fieldName,
    operator: 'eq',
    value: normalizePrimitiveRuleValue(condition, fieldMeta),
  };
}

function parseArrayClauses(clauses: unknown[], modelDefinition?: DbModelDefinition | null): FilterNode[] {
  return clauses
    .map((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return undefined;
      }

      const parsed = parseWhereObject(entry as Record<string, unknown>, modelDefinition);
      if (parsed.children.length === 0) return undefined;
      if (parsed.children.length === 1 && parsed.logic === 'AND') return parsed.children[0];
      return parsed;
    })
    .filter((node): node is FilterNode => Boolean(node));
}

function parseWhereObject(whereObj: Record<string, unknown>, modelDefinition?: DbModelDefinition | null): FilterGroup {
  const onlyAnd = Object.keys(whereObj).length === 1 && Array.isArray(whereObj.AND);
  const onlyOr = Object.keys(whereObj).length === 1 && Array.isArray(whereObj.OR);

  if (onlyAnd) {
    return {
      ...createEmptyFilterGroup('AND'),
      children: parseArrayClauses(whereObj.AND as unknown[], modelDefinition),
    };
  }

  if (onlyOr) {
    return {
      ...createEmptyFilterGroup('OR'),
      children: parseArrayClauses(whereObj.OR as unknown[], modelDefinition),
    };
  }

  const children: FilterNode[] = [];

  if (Array.isArray(whereObj.AND)) {
    const parsedAnd = parseArrayClauses(whereObj.AND, modelDefinition);
    if (parsedAnd.length > 0) {
      children.push({
        ...createEmptyFilterGroup('AND'),
        children: parsedAnd,
      });
    }
  }

  if (Array.isArray(whereObj.OR)) {
    const parsedOr = parseArrayClauses(whereObj.OR, modelDefinition);
    if (parsedOr.length > 0) {
      children.push({
        ...createEmptyFilterGroup('OR'),
        children: parsedOr,
      });
    }
  }

  Object.entries(whereObj).forEach(([fieldName, rawCondition]) => {
    if (fieldName === 'AND' || fieldName === 'OR' || fieldName === 'NOT') return;
    const rule = parseConditionRule(fieldName, rawCondition, modelDefinition);
    if (rule) children.push(rule);
  });

  return {
    ...createEmptyFilterGroup('AND'),
    children,
  };
}

export function parsePrismaWhere(where: unknown, modelDefinition?: DbModelDefinition | null): FilterGroup {
  if (!where || typeof where !== 'object' || Array.isArray(where)) {
    return createEmptyFilterGroup('AND');
  }

  const parsed = parseWhereObject(where as Record<string, unknown>, modelDefinition);
  if (parsed.children.length === 0) {
    return createEmptyFilterGroup('AND');
  }

  return parsed;
}

export function serializeWhere(where: Record<string, unknown> | undefined): string | null {
  if (!where || Object.keys(where).length === 0) return null;
  return encodeURIComponent(JSON.stringify(where));
}

function parseWhereObjectCandidate(rawValue: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function deserializeWhere(rawValue: string | null): Record<string, unknown> | undefined {
  if (!rawValue) return undefined;

  const directParsed = parseWhereObjectCandidate(rawValue);
  if (directParsed) return directParsed;

  try {
    const decoded = decodeURIComponent(rawValue);
    const decodedParsed = parseWhereObjectCandidate(decoded);
    return decodedParsed ?? undefined;
  } catch {
    return undefined;
  }
}

export function countFilterRules(group: FilterGroup): number {
  return group.children.reduce((count, node) => {
    if (isRule(node)) return count + 1;
    return count + countFilterRules(node);
  }, 0);
}

export function filterSummary(group: FilterGroup): string {
  const ruleCount = countFilterRules(group);
  if (ruleCount === 0) return 'Sin filtros aplicados';
  if (ruleCount === 1) return '1 regla';
  return `${ruleCount} regla(s) - logica raiz ${group.logic}`;
}
